'use client';

import { ReactNode, CSSProperties } from 'react';
import { useTranslations } from 'next-intl';
import { useRouter, useSearchParams } from 'next/navigation';
import styles from './Tabs.module.css';

export interface TabItem {
    id: string;
    labelKey: string;
    content: ReactNode;
    disabled?: boolean;
}

export interface TabsProps {
    tabs: TabItem[];
    defaultTab?: string;
    onChange?: (tabId: string) => void;
    className?: string;
}

type Vars = CSSProperties & { ['--index']?: number };

export default function Tabs({
    tabs,
    defaultTab,
    onChange,
    className = ''
}: TabsProps) {
    const t = useTranslations();
    const searchParams = useSearchParams();
    const router = useRouter();

    const tabParam = searchParams.get('tab');
    const activeTab = tabParam && tabs.some(tab => tab.id === tabParam)
        ? tabParam
        : (defaultTab || tabs[0]?.id || '');

    const activeIndex = Math.max(0, tabs.findIndex(t => t.id === activeTab));

    const handleTabClick = (tabId: string) => {
        const tab = tabs.find(t => t.id === tabId);
        if (tab && !tab.disabled) {
            router.push(`?tab=${tabId}`);
            onChange?.(tabId);
        }
    };

    const activeTabContent = tabs.find(tab => tab.id === activeTab)?.content;

    return (
        <div className={`${styles.container} ${className}`}>
            <div className={styles.tabList}>
                <div className={styles.tabList__rail} style={{ '--index': activeIndex } as Vars} role="tablist">
                    <div className={styles.tabList__slider} aria-hidden="true" />
                    <div className={styles.tabList__tabs}>
                        {tabs.map((tab) => (
                            <button
                                key={tab.id}
                                className={`${styles.tab} ${
                                    activeTab === tab.id ? styles.tab__active : ''
                                } ${tab.disabled ? styles.tab__disabled : ''}`}
                                onClick={() => handleTabClick(tab.id)}
                                disabled={tab.disabled}
                                role="tab"
                                aria-selected={activeTab === tab.id}
                                aria-controls={`tabpanel-${tab.id}`}
                                id={`tab-${tab.id}`}
                                type="button"
                            >
                                {t(tab.labelKey)}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            <div id={`tabpanel-${activeTab}`}
                role="tabpanel"
                aria-labelledby={`tab-${activeTab}`}
                className={styles.tabContent}>
                {activeTabContent}
            </div>
        </div>
    );
}